import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Award, LifeBuoy, UserCog, LogOut, DollarSign, Menu, ShieldCheck,
} from "lucide-react";
import {
  Sheet, SheetContent, SheetTrigger,
} from "./ui/sheet";

/**
 * Mission Watch — mobile navigation drawer.
 *
 * Visible on < sm viewports only. The dashboard header hides its inline nav
 * (`hidden sm:flex`) at that breakpoint and renders this hamburger instead,
 * which opens a right-side sheet with full labels for every destination.
 *
 * Bonus: a **right-edge swipe** (finger start within 24px of the right edge,
 * swipe > 60px leftward, within 500ms) also opens the drawer — matching the
 * iOS/Android platform gesture power users expect. Small target on the
 * hamburger becomes optional rather than mandatory.
 *
 * Rationale: icon-only nav on mobile made secondary users (board members,
 * IT staff helping with setup) guess what each icon meant. Labels-in-a-sheet
 * keeps discoverability high while the collapsed hamburger keeps the header
 * itself narrow enough to avoid horizontal overflow.
 */
export function DashboardMobileNav({
  user,
  milestonesUnseen,
  onOpenAccount,
  onSignOut,
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const swipeStartRef = useRef(null);

  // Right-edge-swipe → open. Listens at the window level so we catch the
  // gesture regardless of which element is under the finger when it lands.
  // No-op on desktop / when the sheet is already open / on non-touch devices.
  useEffect(() => {
    const EDGE_PX = 24;      // finger must start within this many px of the right edge
    const MIN_DIST = 60;     // must move at least this far leftward
    const MAX_MS = 500;      // and complete the swipe within this window
    const MAX_VERT = 60;     // ignore mostly-vertical swipes (page scrolls)

    const onTouchStart = (e) => {
      if (open) return;
      // Only listen at sub-sm viewports where the drawer even makes sense.
      if (window.innerWidth >= 640) return;
      const t = e.touches?.[0];
      if (!t) return;
      const rightGap = window.innerWidth - t.clientX;
      if (rightGap > EDGE_PX) return;
      swipeStartRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    };
    const onTouchEnd = (e) => {
      const start = swipeStartRef.current;
      swipeStartRef.current = null;
      if (!start) return;
      const t = e.changedTouches?.[0];
      if (!t) return;
      const dx = start.x - t.clientX;
      const dy = Math.abs(start.y - t.clientY);
      const dt = Date.now() - start.t;
      if (dx > MIN_DIST && dy < MAX_VERT && dt < MAX_MS) {
        setOpen(true);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [open]);

  const close = () => setOpen(false);

  // Swipe-to-close: a rightward finger swipe anywhere inside the sheet
  // dismisses it. Mirrors the right-edge open swipe so both directions
  // work symmetrically — users never need to hit the small X button.
  const closeSwipeRef = useRef(null);
  const onSheetTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    closeSwipeRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onSheetTouchEnd = (e) => {
    const start = closeSwipeRef.current;
    closeSwipeRef.current = null;
    if (!start) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - start.x;                 // positive = rightward
    const dy = Math.abs(t.clientY - start.y);
    const dt = Date.now() - start.t;
    if (dx > 60 && dy < 60 && dt < 500) close();
  };

  const linkClass =
    "flex items-center gap-3 px-4 py-3.5 rounded-xl text-charcoal-800 hover:bg-charcoal-50 active:bg-charcoal-100 transition-colors text-[15px]";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-charcoal-700 hover:bg-charcoal-100 transition-colors"
          data-testid="mobile-nav-toggle"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="w-[85%] max-w-sm p-0 flex flex-col bg-white"
        data-testid="mobile-nav-sheet"
        onTouchStart={onSheetTouchStart}
        onTouchEnd={onSheetTouchEnd}
      >
        {/* Header — brand + close */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-charcoal-900 grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-semibold tracking-tight text-charcoal-900 text-[15px]">
              Mission Watch
            </span>
          </div>
          {/* Close button is provided by the shadcn <SheetContent> primitive
              (absolute top-right). We used to render a second custom X here,
              which produced two overlapping close controls on mobile. Removed. */}
        </div>

        {/* User identity chip */}
        {user?.email && (
          <div className="px-5 pt-5 pb-3">
            <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-charcoal-400 mb-1">
              Signed in as
            </p>
            <p className="text-sm text-charcoal-700 truncate" data-testid="mobile-nav-email">
              {user.email}
            </p>
          </div>
        )}

        {/* Nav — mirrors the desktop header items in the same order */}
        <nav className="flex-1 px-3 pt-2 pb-3 space-y-1">
          <Link
            to="/milestones"
            onClick={close}
            className={linkClass}
            data-testid="mobile-nav-milestones"
          >
            <Award className="w-5 h-5 text-charcoal-500" />
            <span className="flex-1">Milestones</span>
            {milestonesUnseen > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-emerald-500 text-white text-[11px] font-bold leading-none tabular-nums"
                data-testid="mobile-nav-milestones-badge"
              >
                {milestonesUnseen > 9 ? "9+" : milestonesUnseen}
              </span>
            )}
          </Link>

          <a
            href="mailto:admin@missionwatch.app?subject=Mission%20Watch%20Support"
            onClick={close}
            className={linkClass}
            data-testid="mobile-nav-support"
          >
            <LifeBuoy className="w-5 h-5 text-charcoal-500" />
            <span>Support</span>
          </a>

          {user?.is_owner && (
            <Link
              to="/admin/cost"
              onClick={close}
              className={linkClass}
              data-testid="mobile-nav-cost"
            >
              <DollarSign className="w-5 h-5 text-brand" />
              <span className="text-brand">Cost dashboard</span>
            </Link>
          )}

          <button
            type="button"
            onClick={() => {
              close();
              onOpenAccount?.();
            }}
            className={linkClass + " w-full text-left"}
            data-testid="mobile-nav-account"
          >
            <UserCog className="w-5 h-5 text-charcoal-500" />
            <span>Account</span>
          </button>
        </nav>

        {/* Footer — sign out is destructive-adjacent, so it lives visually apart */}
        <div className="px-3 pb-6 pt-3 border-t border-black/5">
          <button
            type="button"
            onClick={() => {
              close();
              onSignOut?.();
              navigate("/");
            }}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-charcoal-700 hover:bg-red-50 hover:text-red-700 active:bg-red-100 transition-colors text-[15px]"
            data-testid="mobile-nav-signout"
          >
            <LogOut className="w-5 h-5" />
            <span>Sign out</span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
